import { AccountCurrencyCode } from '@lune-climate/lune'
import { Text, Tooltip } from '@lune-fe/lune-ui-lib'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Stack } from '@mui/material'
import FieldValidationError from 'lune-ui-lib/src/components/FieldValidationError'
import React, { useEffect, useState } from 'react'

import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import ParameterMapping from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import { MappingDataSheet, PropertyMapping } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    PARAMETER_MAPPING_OUTPUT,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'

export type ClientAccountMappingResult = {
    clientAccountName: PropertyMapping<string>
    clientAccountCurrency?: PropertyMapping<AccountCurrencyCode>
}

export type SheetData = {
    sheet: MappingDataSheet
    headers: string[]
    exampleRow: string[]
    headerStartIndex: number
    headerRow: number
    endRow: number
}

const ClientAccountMappingFields = ({
    sheetData,
    initialClientAccountName,
    initialClientAccountCurrency,
    onSubmit,
    formRef,
}: {
    sheetData: SheetData
    initialClientAccountName?: PropertyMapping<string>
    initialClientAccountCurrency?: PropertyMapping<AccountCurrencyCode>
    onSubmit: (result: ClientAccountMappingResult) => void
    formRef: React.RefObject<HTMLFormElement>
}) => {
    const [clientAccountNameMapping, setClientAccountNameMapping] = useState<
        PropertyMapping<string> | undefined
    >(initialClientAccountName)
    const [clientAccountCurrencyMapping, setClientAccountCurrencyMapping] = useState<
        PropertyMapping<AccountCurrencyCode> | undefined
    >(initialClientAccountCurrency)
    const [clientAccountNameColumnAutoFilled, setClientAccountNameColumnAutoFilled] =
        useState<boolean>(false)
    const [clientAccountCurrencyColumnAutoFilled, setClientAccountCurrencyColumnAutoFilled] =
        useState<boolean>(false)
    const [clientAccountCurrencyMappingAutoFilled, setClientAccountCurrencyMappingAutoFilled] =
        useState<boolean>(false)
    const [error, setError] = useState<string | undefined>()

    useEffect(() => {
        if (!clientAccountNameMapping) {
            setError('Client account name mapping is required')
        } else {
            setError(undefined)
        }
    }, [clientAccountNameMapping])

    // TODO ParameterMapping expects RFQTemplateState but only uses  sheet-related fields so
    // we cast here. Types should be narrower and `state` shouldn't be passed around so freely
    const state = sheetData as unknown as RFQTemplateState

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!clientAccountNameMapping) {
            setError('Client account name mapping is required')
            return
        }
        onSubmit({
            clientAccountName: clientAccountNameMapping,
            clientAccountCurrency: clientAccountCurrencyMapping,
        })
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit}>
            <Stack key="clientAccountDetails" spacing="72px" sx={{ mt: 4, mb: 3 }}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Text variant="h6">Client accounts</Text>
                        <Tooltip
                            title="These mappings are only used when 'Multiple client accounts' is selected during upload."
                            placement="top"
                            contentWidth={true}
                        >
                            <Box p={1.5} sx={{ cursor: 'pointer' }}>
                                <InfoOutlinedIcon />
                            </Box>
                        </Tooltip>
                    </Stack>
                    <Box sx={{ mt: 4 }}>
                        <Labels />
                    </Box>
                    <ParameterMapping<string>
                        state={state}
                        parameter="clientAccountName"
                        mappingType="direct"
                        outputValues={[]}
                        formatOutput={String}
                        propertyMapping={clientAccountNameMapping}
                        onUpdate={(_parameterName, data) => {
                            setClientAccountNameMapping(data)
                        }}
                        autoFill={{
                            column: {
                                value: clientAccountNameColumnAutoFilled,
                                update: setClientAccountNameColumnAutoFilled,
                            },
                            mapping: { value: false, update: () => {} },
                        }}
                    />
                    <ParameterMapping<AccountCurrencyCode>
                        state={state}
                        parameter="clientAccountCurrency"
                        mappingType="mapping"
                        outputValues={PARAMETER_MAPPING_OUTPUT.clientAccountCurrency}
                        formatOutput={String}
                        propertyMapping={clientAccountCurrencyMapping}
                        onUpdate={(_parameterName, data) => {
                            setClientAccountCurrencyMapping(data)
                        }}
                        autoFill={{
                            column: {
                                value: clientAccountCurrencyColumnAutoFilled,
                                update: setClientAccountCurrencyColumnAutoFilled,
                            },
                            mapping: {
                                value: clientAccountCurrencyMappingAutoFilled,
                                update: setClientAccountCurrencyMappingAutoFilled,
                            },
                        }}
                    />
                    {error && <FieldValidationError error={error} />}
                </Box>
            </Stack>
        </form>
    )
}

export default ClientAccountMappingFields
